import type { PhoneNumber } from "@repo/types";
import { PhoneFormField } from "./PhoneFormField";
import { Button } from "@repo/ui/components/Base/Button";
import { CircleMinusIcon, CirclePlusIcon } from "lucide-react";
import { useState } from "react";

export const MultiPhoneFormField = ({
  phoneNumbers,
  fieldName,
  setFieldValue,
  disabled = false,
}: {
  phoneNumbers: PhoneNumber | PhoneNumber[];
  fieldName: string;
  setFieldValue: (
    field: string,
    value: PhoneNumber[],
    shouldValidate?: boolean | undefined
  ) => void;
  disabled?: boolean;
}) => {
  // if phoneNumbers is not an array, initialize with Work / Mobile / Home phone options
  if (!Array.isArray(phoneNumbers)) {
    const numbers = [
      { type: "work", number: "" },
      { type: "mobile", number: "" },
      { type: "home", number: "" },
    ];
    if (phoneNumbers.number) {
      let found = phoneNumbers.type
        ? numbers.find(
            (number) => number.type === (phoneNumbers as PhoneNumber).type
          )
        : numbers[0];
      if (!found) numbers.push((found = phoneNumbers));
      found.number = phoneNumbers.number;
    }
    phoneNumbers = numbers;
  }

  // mutation methods
  const update = (numbers: PhoneNumber[]) => {
    setFieldValue(fieldName, numbers);
  };

  const updateNumber = (index: number, value: PhoneNumber) => {
    const newNumbers = phoneNumbers.slice();
    newNumbers[index] = value;
    update(newNumbers);
  };
  const addNumber = (value: PhoneNumber) => {
    const newNumbers = [...phoneNumbers, value];
    update(newNumbers);
  };
  const removeNumber = (index: number) => {
    const newNumbers = phoneNumbers.filter((_, i) => i !== index);
    update(newNumbers);
  };

  const [validate, setValidate] = useState(false);

  return (
    <div>
      {phoneNumbers.map((phoneNumber, index) => {
        return (
          <div key={index} className="flex flex-row items-start gap-x-2 mb-2">
            <PhoneFormField
              disabled={disabled}
              className="flex-grow"
              phoneNumber={phoneNumber}
              fieldName=""
              setFieldValue={(_, value) => {
                updateNumber(index, value);
              }}
              showErrors={validate}
            />
            {!disabled && (
              <Button
                variant="ghost"
                className="w-10 p-1 mt-[30px]"
                onClick={() => {
                  removeNumber(index);
                }}
              >
                <CircleMinusIcon />
              </Button>
            )}
          </div>
        );
      })}
      <div className="flex flex-row items-center gap-x-2">
        <Button
          disabled={disabled}
          variant="ghost"
          className="px-2"
          onClick={() => {
            // only allow adding more if all are valid
            for (const number of phoneNumbers) {
              if (!number.type || !number.number) {
                setValidate(true);
                return;
              }
            }

            setValidate(false);
            addNumber({ type: "", number: "" });
          }}
        >
          <CirclePlusIcon className="mr-2" />
          Add Phone Number
        </Button>
      </div>
    </div>
  );
};
